import React, { useState } from 'react';
import Button from '../Button';
import SparkleIcon from '../CustomIcons/SparkleIcon';
import Tag from '../Tag';
import Tooltip from '../Tooltip';
import { Typography } from '../Typography';

export type ChangeKind = 'changed' | 'added' | 'removed';

export interface ChangeReviewItemProps {
    /**
     * What changed — a field name, or the name of a group of them. Omit it when the field this
     * item annotates already carries its own visible heading elsewhere on the page (so the name
     * doesn't render twice) — pass `fieldName` instead, so the badge and actions still know what
     * they belong to.
     */
    label?: string;
    /**
     * Names the field the badge and actions act on, for assistive tech. Defaults to `label`, so
     * passing only `label` keeps working as before. Set it explicitly when `label` is omitted —
     * otherwise "Accept"/"Revert" would have nothing telling a screen reader which field they act
     * on.
     */
    fieldName?: string;
    /** How it read before. A node, so a caller can show chips or code rather than a string. */
    before?: React.ReactNode;
    /** How it reads now. Usually omitted: the field itself is showing it. */
    after?: React.ReactNode;
    kind?: ChangeKind;
    /** What made the change. Names the assistant by default. */
    origin?: string;
    /**
     * The field the change is annotating. When given, the tag/label/actions form a header band
     * above it and the before/after line moves below it, so "Was …" reads next to the value it
     * describes instead of stacked with the header. Omit it for the standalone card.
     */
    children?: React.ReactNode;
    onApprove: () => void;
    onDeny: () => void;
    /**
     * Fires while the revert control is hovered or focused, and again when it stops being
     * either. A callback rather than internal state because the thing being previewed — the
     * field this change came from — lives elsewhere on the page, outside what `Item` renders;
     * only the caller can swap what that field shows.
     */
    onPreviewRevert?: (previewing: boolean) => void;
    disabled?: boolean;
    className?: string;
}

const KIND_WORDS: Record<ChangeKind, string> = {
    changed: 'Changed',
    added: 'Added',
    removed: 'To be removed',
};

/**
 * What the "AI" pill means, for its tooltip. The badge is two letters, which says who wrote
 * the value but not that the reader is expected to do something about it — and the accept and
 * revert controls it refers to are hidden until the row is hovered or focused.
 */
const badgeTooltip = (kind: ChangeKind, origin: string): string =>
    kind === 'removed'
        ? `Marked for removal by ${origin}. Accept to remove it, or revert to keep it.`
        : `${KIND_WORDS[kind]} by ${origin}. Accept to keep it, or revert to restore the previous value.`;

// A multi-select with nothing chosen reaches this as `[]`, not `undefined` — that, a blank or
// whitespace-only string, and an absent value all read as "nothing was there" rather than
// printing nothing (or, worse, the literal word "undefined").
const isEmptyDisplacedValue = (value: React.ReactNode): boolean => {
    if (value === undefined || value === null) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    return false;
};

/**
 * Says that something on screen was changed by a machine, shows what it displaced, and offers
 * the two ways out.
 *
 * Accept is deliberately the heavier control (filled, dark) against Revert's quiet outline —
 * the design leans toward accepting the assistant's change rather than presenting both as
 * equal options. That is the designer's call to make, not this component's.
 *
 * The actions are always visible. Revealing them on hover was tried and dropped: a change
 * waiting on a decision is the point of the row, and hiding the only two controls that
 * resolve it made them easy to miss — and unreachable on a touch screen, which has no hover
 * to reveal them with.
 */
const ChangeReviewItem: React.FC<ChangeReviewItemProps> = ({
    label,
    fieldName,
    before,
    after,
    kind = 'changed',
    origin = 'assistant',
    children,
    onApprove,
    onDeny,
    onPreviewRevert,
    disabled = false,
    className = '',
}) => {
    const [isPreviewingRevert, setIsPreviewingRevert] = useState(false);
    const accessibleFieldName = fieldName ?? label;

    // No-ops when `onPreviewRevert` is absent, which also keeps `isPreviewingRevert` from ever
    // flipping true — so the hint below never renders unless a caller asked for the feature.
    const startRevertPreview = () => {
        if (onPreviewRevert === undefined) {
            return;
        }
        setIsPreviewingRevert(true);
        onPreviewRevert(true);
    };

    const endRevertPreview = () => {
        if (onPreviewRevert === undefined) {
            return;
        }
        setIsPreviewingRevert(false);
        onPreviewRevert(false);
    };

    const badgeAndLabel = (
        <div className="flex flex-wrap items-center gap-2">
            {label !== undefined && (
                <Typography.Text size="small" fontWeight="semi-bold">
                    {label}
                </Typography.Text>
            )}
            {/* The pill itself only says "AI" — the fuller "Changed by <origin>" phrase that
                names who made the change lives in the sr-only span beside it, so a screen
                reader still hears the origin without the visible badge repeating it. */}
            <Tooltip text={badgeTooltip(kind, origin)}>
                {/* `aria-hidden` stays on the pill: the sr-only span below already carries the
                    origin, and the tooltip repeats it for a reader who only has the badge. */}
                <span aria-hidden="true">
                    <Tag
                        type="assistant"
                        value="AI"
                        icon={<SparkleIcon size={11} />}
                        fontSize="xsmall"
                        paddingSize="compact"
                        textTransform="none"
                    />
                </span>
            </Tooltip>
            <span className="sr-only">{`${KIND_WORDS[kind]} by ${origin}`}</span>
        </div>
    );

    const displaysChange = before !== undefined || after !== undefined;

    const beforeAfter = (
        <>
            {displaysChange && (
                <div className="flex flex-wrap items-baseline gap-1">
                    <Typography.SecondaryText size="small">
                        {/* Struck through, so a glance separates what it was from what it is
                            without having to read the labels. */}
                        Was {isEmptyDisplacedValue(before) ? <i>empty</i> : <s>{before}</s>}
                        {after !== undefined ? ' →' : null}
                    </Typography.SecondaryText>
                    {after !== undefined && (
                        <Typography.Text
                            size="small"
                            style={{ color: 'var(--malachite-assistant-foreground)' }}
                        >
                            {after}
                        </Typography.Text>
                    )}
                </div>
            )}
            {isPreviewingRevert && (
                <Typography.SecondaryText size="small">
                    Previewing the revert — release to keep the AI value
                </Typography.SecondaryText>
            )}
        </>
    );

    // Always visible, not revealed on hover: a change waiting on a decision is the point of
    // the row, and hiding the only two controls that resolve it behind a hover made them easy
    // to miss entirely — and unreachable on a touch screen.
    const actions = (
        <div className="flex flex-shrink-0 items-center gap-1">
            <Button
                // `surface`, not `outline`: this sits on the assistant's tinted field, and a
                // transparent button on a tint stops reading as a button.
                variant="surface"
                size="xsmall"
                aria-label={
                    accessibleFieldName === undefined
                        ? 'Revert change'
                        : `Revert ${accessibleFieldName}`
                }
                disabled={disabled}
                onClick={onDeny}
                onMouseEnter={startRevertPreview}
                onMouseLeave={endRevertPreview}
                onFocus={startRevertPreview}
                onBlur={endRevertPreview}
                // The variant's own hover only darkens the border to grey; the mock wants
                // Revert's border and text to pick up the assistant violet specifically.
                className="hover:border-[var(--malachite-assistant-foreground)] hover:text-[var(--malachite-assistant-foreground)]"
            >
                {/* Literal glyphs, not icon components: the mock sets them inline with the
                    label at 11px, and no bundled icon reads that small without looking like a
                    different weight than the text beside it. Reload (not a cross) because
                    reverting restores the previous value rather than dismissing the field.
                    The gap is a margin rather than a space: the button lays its children out
                    with flex, which strips the leading whitespace off a bare text node. */}
                <span aria-hidden="true" className="mr-1">
                    ↺
                </span>
                Revert
            </Button>
            <Button
                variant="filled"
                size="xsmall"
                aria-label={
                    accessibleFieldName === undefined
                        ? 'Accept change'
                        : `Accept ${accessibleFieldName}`
                }
                disabled={disabled}
                onClick={onApprove}
            >
                <span aria-hidden="true" className="mr-1">
                    ✓
                </span>
                Accept
            </Button>
        </div>
    );

    // With a field to wrap, the header (tag/label/actions) and the before/after line split
    // to opposite sides of `children` instead of sitting together above it — see the
    // component doc comment on `children`.
    if (children !== undefined) {
        return (
            <div
                className={`flex flex-col gap-2 rounded-lg p-2 ${className}`}
                style={{ background: 'var(--malachite-tag-assistant-background)' }}
            >
                <div className="flex flex-wrap items-center justify-between gap-2">
                    {badgeAndLabel}
                    {actions}
                </div>
                {children}
                <div className="flex flex-col gap-1">{beforeAfter}</div>
            </div>
        );
    }

    return (
        <div
            className={`flex items-start gap-2 rounded-lg p-2 ${className}`}
            style={{ background: 'var(--malachite-tag-assistant-background)' }}
        >
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                {badgeAndLabel}
                {beforeAfter}
            </div>
            {actions}
        </div>
    );
};

export default ChangeReviewItem;
export { ChangeReviewItem };
